import { useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  Bookmark,
  Calendar,
  ChevronRight,
  ExternalLink,
  GraduationCap,
  User,
  VideoOff,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { formatInstituteDate } from '@/features/teacher/utils';
import type { UnifiedLecture } from './lectureData';
import { getLectureSubjectTheme } from './lectureTheme';
import { LectureThumbnail } from './LectureThumbnail';

interface LecturePlayerViewProps {
  lecture: UnifiedLecture;
  onBack: () => void;
  relatedLectures: UnifiedLecture[];
  onSelectLecture: (lecture: UnifiedLecture) => void;
  isSaved?: boolean;
  onToggleSave?: (lectureUuid: string) => void;
  onMarkWatched?: (lectureUuid: string) => void;
  /** Whose class this is, when the reader is not the child themselves. */
  childName?: string;
}

/** How many other lessons the rail offers before it stops being a shortlist. */
const RELATED_LIMIT = 6;

/**
 * Watching one recorded lesson.
 *
 * This screen used to *simulate* a player: a hardcoded 26:15 duration, a timer
 * counting up on its own, a drawn cartoon of a teacher, invented captions and
 * six chapters given to every lesson whatever it was about. It never touched
 * the video, so the recording a teacher actually uploaded could not be watched
 * at all. It plays the real thing now.
 *
 * Two kinds of lesson arrive, and they are played differently:
 *
 *   UPLOAD  the institute's own file in its private bucket. The server mints a
 *           short-lived signed URL per viewer, per request (CLAUDE.md §11), and
 *           it goes straight into a `<video>` element.
 *   LINK    somebody else's — YouTube, Drive, Zoom. Only hosts we know how to
 *           frame are framed; everything else is offered as a link out rather
 *           than dropped into an iframe that would refuse to load anyway.
 *
 * The controls are the browser's own. A hand-rolled bar is how keyboard
 * operation, captions, picture-in-picture, AirPlay and the speed menu each get
 * half-implemented; `controls` gives every one of them, correctly, for free.
 */
export function LecturePlayerView({
  lecture,
  onBack,
  relatedLectures,
  onSelectLecture,
  isSaved = false,
  onToggleSave,
  onMarkWatched,
  childName,
}: LecturePlayerViewProps) {
  const whoseClass = childName ? `${childName}'s class` : 'your class';
  const theme = getLectureSubjectTheme(lecture.subject);
  const formattedDate = formatInstituteDate(lecture.date);

  const playback = useMemo(() => resolvePlayback(lecture), [lecture]);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Autoplay video when opened
  useEffect(() => {
    if (playback.kind === 'FILE' && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Handled if browser restricts unmuted autoplay
        });
      }
    }
  }, [playback]);

  // Opening the lesson is what counts as watching it.
  useEffect(() => {
    onMarkWatched?.(lecture.uuid);
  }, [lecture.uuid, onMarkWatched]);

  return (
    <div className="flex flex-col gap-5 max-w-full">
      {/* --- HEADER ----------------------------------------------------------- */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-pill text-xs font-bold text-ink bg-surface border border-line hover:bg-surface-2 hover:border-accent/40 shadow-xs transition-all cursor-pointer group active:scale-98"
          >
           <ArrowLeft className="h-4 w-4 text-accent group-hover:-translate-x-0.5 transition-transform" />
          <span>Back to Recorded Classes</span>
           
          </button>
</div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex flex-col gap-2 min-w-0">
        
          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
            <h1 className="text-base sm:text-2xl font-black text-slate-700 dark:text-white tracking-tight leading-tight">
              {lecture.title}
            </h1>
            <span
              className={`px-2.5 sm:px-3 py-0.5 rounded-full text-[10px] sm:text-xs font-bold tracking-wider border ${theme.pill}`}
            >
              {lecture.subject}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[11px] sm:text-[12px] text-slate-500 dark:text-slate-400 font-semibold">
            <span className="flex items-center gap-1 sm:gap-1.5">
              <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400" />
              {formattedDate}
            </span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span className="flex items-center gap-1 sm:gap-1.5">
              <User className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400" />
              {lecture.teacher}
            </span>
            {lecture.className && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <GraduationCap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400" />
                  {lecture.className}
                </span>
              </>
            )}
            {lecture.duration && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span>{lecture.duration}</span>
              </>
            )}
          </div>
        </div>

        {onToggleSave && (
          <button
            type="button"
            onClick={() => onToggleSave(lecture.uuid)}
            aria-pressed={isSaved}
            className={`flex shrink-0 items-center gap-1.5 self-start px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-sm sm:text-base font-bold border transition-all cursor-pointer ${
              isSaved
                ? 'bg-[#4f46e5] text-white border-[#4f46e5] shadow-sm'
                : 'bg-white dark:bg-[#151928] text-slate-700 dark:text-slate-300 border-indigo-500/40 dark:border-white/10 hover:border-indigo-500/40'
            }`}
          >
            <Bookmark className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isSaved ? 'fill-current' : ''}`} />
            <span>{isSaved ? 'Saved' : 'Save'}</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
        {/* --- THE LESSON ----------------------------------------------------- */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          <div className="rounded-[24px] bg-[#0d121f] border border-slate-200/80 dark:border-white/10 shadow-xs overflow-hidden">
            <div className="relative aspect-video w-full bg-black">
              {playback.kind === 'FILE' && (
                /* `key` on the source so switching lessons reloads the element
                   rather than leaving the previous video playing underneath. */
                <video
                  ref={videoRef}
                  autoPlay
                  key={playback.url}
                  src={playback.url}
                  controls
                  controlsList="nodownload"
                  playsInline
                  className="h-full w-full"
                >
                  Your browser cannot play this recording.
                </video>
              )}

              {playback.kind === 'EMBED' && (
                <iframe
                  key={playback.url}
                  src={playback.url}
                  title={lecture.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="h-full w-full border-0"
                />
              )}

              {playback.kind === 'EXTERNAL' && (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white">
                    <ExternalLink className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-bold text-white">
                    This recording is hosted elsewhere
                  </p>
                  <p className="max-w-sm text-xs font-medium text-white/70">
                    Your teacher linked it from another site, which does not allow playing
                    inside the portal.
                  </p>
                  <a
                    href={playback.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#4f46e5] px-3.5 py-2 text-xs font-bold text-white transition-colors hover:bg-indigo-700"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    Open the recording
                  </a>
                </div>
              )}

              {playback.kind === 'NONE' && (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white/70">
                    <VideoOff className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-bold text-white">This recording is not available</p>
                  <p className="max-w-sm text-xs font-medium text-white/70">
                    The video is missing or the link has expired. Tell your class teacher and
                    they can post it again.
                  </p>
                </div>
              )}
            </div>
          </div>

          {lecture.description ? (
            <div className="rounded-[24px] bg-white dark:bg-[#151928] border border-slate-200/80 dark:border-white/10 p-4 sm:p-5 shadow-xs">
              <h2 className="text-sm font-extrabold text-slate-800 dark:text-white">
                About this class
              </h2>
              <p className="mt-1.5 text-[13px] leading-relaxed font-medium text-slate-600 dark:text-slate-300 whitespace-pre-line">
                {lecture.description}
              </p>
            </div>
          ) : null}
        </div>

        {/* --- OTHER LESSONS -------------------------------------------------- */}
        <div className="lg:col-span-4 flex flex-col rounded-[24px] bg-white dark:bg-[#151928] border border-slate-200/80 dark:border-white/10 shadow-xs overflow-hidden">
          <div className="border-b border-slate-100 dark:border-white/10 px-4 py-3">
            <h2 className="text-sm font-extrabold text-slate-800 dark:text-white">
              More from {whoseClass}
            </h2>
          </div>

          {relatedLectures.length === 0 ? (
            <div className="p-4">
              <Alert tone="info" title="Nothing else yet">
                This is the only recording posted to {whoseClass} so far.
              </Alert>
            </div>
          ) : (
            <div className="flex flex-col p-3 sm:p-4 gap-2.5 max-h-[500px] overflow-y-auto">
              {relatedLectures.slice(0, RELATED_LIMIT).map((related) => {
                const relatedTheme = getLectureSubjectTheme(related.subject);

                return (
                  <button
                    key={related.uuid}
                    type="button"
                    onClick={() => onSelectLecture(related)}
                    className="group flex w-full items-center gap-3 p-2.5 sm:p-3 rounded-2xl bg-slate-50/70 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 hover:border-indigo-500/30 transition-all cursor-pointer text-left"
                  >
                    <div className="relative h-12 w-16 sm:h-14 sm:w-20 rounded-xl bg-slate-900 overflow-hidden shrink-0 flex items-center justify-center">
                      <LectureThumbnail theme={related.thumbnailTheme} title={related.title} />
                      {related.duration && (
                        <div className="absolute bottom-1 right-1 px-1 rounded bg-black/80 text-[9px] font-bold text-white">
                          {related.duration}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col min-w-0 flex-1">
                      <span
                        className={`w-fit px-2 rounded-full text-[9px] font-extrabold uppercase tracking-wider border ${relatedTheme.pill} mb-1`}
                      >
                        {related.subject}
                      </span>
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">
                        {related.title}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-400 mt-0.5 truncate">
                        {related.teacher} • {formatInstituteDate(related.date)}
                      </span>
                    </div>

                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** What the player should actually put on screen for this lesson. */
type Playback =
  /** The institute's own file, behind a signed URL. */
  | { kind: 'FILE'; url: string }
  /** A host we know how to frame. */
  | { kind: 'EMBED'; url: string }
  /** A link we will not frame — offered as a link out instead. */
  | { kind: 'EXTERNAL'; url: string }
  /** Nothing playable: no key, no link, or a signature that has expired. */
  | { kind: 'NONE' };

function resolvePlayback(lecture: UnifiedLecture): Playback {
  const url = lecture.videoUrl;
  if (!url) return { kind: 'NONE' };

  // An uploaded lesson arrives as a signed URL to our own bucket, so it plays
  // directly. `videoSource` decides this rather than the URL's shape: the
  // server already knows which of the two columns it filled in.
  if (lecture.videoSource === 'UPLOAD') return { kind: 'FILE', url };

  const embed = toEmbedUrl(url);
  return embed ? { kind: 'EMBED', url: embed } : { kind: 'EXTERNAL', url };
}

/**
 * The embeddable address for a link, or null if we should not frame it.
 *
 * Only hosts that publish an embed player are converted. Everything else is
 * sent out to a new tab rather than into an iframe: most sites refuse to be
 * framed at all, and a black rectangle that never loads is worse for a child
 * than a button that opens the video.
 *
 * A malformed address throws out of `new URL`, which is caught and treated as
 * not embeddable — a teacher's typo must not white-screen the panel.
 */
function toEmbedUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  // Only ever https. A link pasted as http would otherwise be framed into a
  // page served over TLS, which the browser blocks as mixed content anyway.
  if (url.protocol !== 'https:') return null;

  const host = url.hostname.replace(/^www\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.slice(1);
    return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1` : null;
  }

  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    // Already an embed address — pass it through rather than rebuilding it.
    if (url.pathname.startsWith('/embed/')) {
      const u = new URL(url.toString());
      u.searchParams.set('autoplay', '1');
      return u.toString();
    }

    const id = url.searchParams.get('v');
    return id ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1` : null;
  }

  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    return /^\d+$/.test(id ?? '') ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
  }

  if (host === 'drive.google.com') {
    // `/file/d/<id>/view` is what Drive's share button gives; `/preview` is the
    // same file in a player rather than in Drive's own chrome.
    const match = url.pathname.match(/^\/file\/d\/([^/]+)/);
    return match ? `https://drive.google.com/file/d/${match[1]}/preview` : null;
  }

  return null;
}
